<template>
  <v-dialog v-model="dialog.show" max-width="800" persistent>
    <v-card class="pa-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-bell</v-icon>
        {{ editMode ? 'Edit Push Notification' : 'Create Push Notification' }}
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-row justify="space-between" class="mb-4">
            <v-col cols="12" sm="12" md="6" align-self="center">
              <p class="secondary--text mb-0">Notification with image?</p>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-switch
                color="primary"
                v-model="notification.withImage"
                flat
              ></v-switch>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col cols="12" sm="12" md="6" align-self="center">
              <p class="secondary--text mb-0">Target Segment</p>
            </v-col>
            <v-col cols="12" sm="12" md="6" style="height: 70px !important;">
              <v-select
                v-model="notification.segment"
                :items="segmentOptions"
                :rules="rules.segment"
                solo
                flat
                background-color="#efefef"
                placeholder="Select segment"
                item-text="text"
                item-value="value"
                
              ></v-select>
            </v-col>
          </v-row>

          <!-- Last login date filter -->
          <v-row justify="space-between" class="mb-4">
            <v-col cols="12" sm="12" md="6" align-self="center">
              <p class="secondary--text mb-0">Filter by last login date</p>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-switch
                color="primary"
                v-model="dateFilterEnabled"
                flat
              ></v-switch>
            </v-col>
          </v-row>
          <v-row v-if="dateFilterEnabled" class="mb-4">
            <v-col cols="12">
              <div class="mb-2">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span
                    class="text-body-2"
                    :class="{ 'font-weight-bold primary--text': !dateFilterByRange }"
                  >Before a date</span>
                  <v-switch
                    v-model="dateFilterByRange"
                    hide-details
                    class="mt-0 flex-shrink-0 mx-2"
                  ></v-switch>
                  <span
                    class="text-body-2"
                    :class="{ 'font-weight-bold primary--text': dateFilterByRange }"
                  >By range</span>
                </div>
                <p class="text-caption mb-2">
                  {{ dateFilterByRange ? 'Select start and end date.' : 'Show users logged in before the selected date.' }}
                </p>
              </div>
              <v-date-picker
                v-if="dateFilterByRange"
                v-model="dateRange"
                range
                no-title
                scrollable
                full-width
                landscape
              ></v-date-picker>
              <v-date-picker
                v-else
                v-model="beforeDate"
                no-title
                scrollable
                full-width
                landscape
              ></v-date-picker>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="12" md="6">
              <v-text-field
                v-model="notification.title"
                :rules="rules.title"
                solo
                flat
                placeholder="Title"
                background-color="#efefef"
                counter
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-text-field
                v-model="notification.link"
                :rules="rules.link"
                solo
                flat
                placeholder="Link (For Example: https://maxprofitness.com/)"
                background-color="#efefef"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-textarea
                v-model="notification.message"
                :rules="rules.message"
                counter="250"
                maxlength="250"
                solo
                flat
                no-resize
                height="250"
                placeholder="Message"
                background-color="#efefef"
              >
              </v-textarea>
            </v-col>
            <v-col v-if="notification.withImage" cols="12" sm="12" md="6">
              <p class="secondary--text mb-2">Notification Image</p>
              <v-card
                color="#efefef"
                class="rounded-lg elevation-0 d-flex justify-center align-center"
                min-width="250"
                min-height="250"
                @click="$refs.notificationImage.$refs.input.click()"
                style="cursor: pointer"
              >
                <v-img
                  v-if="notificationImageSelected.length > 0 && notificationImageSelected[0]"
                  :src="notificationImageSelected[0].img"
                  cover
                  width="250"
                  class="rounded-lg"
                  height="250"
                >
                </v-img>
                <v-icon
                  v-else
                  large
                  color="primary"
                  style="
                    position: absolute;
                    transform: translate(50%, -50%);
                    top: 50%;
                    right: 50%;
                  "
                >
                  mdi-upload
                </v-icon>
              </v-card>
              <p class="caption text-center mt-2">
                Click to upload image (PNG, JPEG)
              </p>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="secondary" text @click="closeDialog" :disabled="loading">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="
            !valid ||
            loading ||
            dateFilterIncomplete ||
            (notification.withImage && notificationImageSelected.length < 1)
          "
          @click="processNotification(false)"
        >
          {{ editMode ? 'Update' : 'Save' }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="
            !valid ||
            loading ||
            dateFilterIncomplete ||
            (notification.withImage && notificationImageSelected.length < 1)
          "
          @click="processNotification(true)"
        >
          {{ editMode ? 'Update and Send' : 'Save and Send' }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Upload Progress Dialog -->
    <v-dialog v-model="dialogUploadImage" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Uploading image, please wait...
          <v-progress-linear
            color="white"
            class="mb-0"
            :value="uploadProgress"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Hidden File Input -->
    <v-file-input
      ref="notificationImage"
      class="d-none"
      color="white"
      accept="image/png, image/jpeg"
      dark
      outlined
      :show-size="1000"
      @change="getNotificationImage"
    />

    <v-snackbar
      v-model="snackbar.snackbar"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
export default {
  name: "PushNotificationModal",
  props: {
    dialog: {
      type: Object,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: null,
    },
    /** When opening from user-list with table date filter: { enabled, byRange, startDate, endDate } */
    initialDateFilter: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    valid: false,
    loading: false,
    dialogUploadImage: false,
    uploadProgress: 0,
    notificationImageSelected: [],
    notification: {
      title: "",
      message: "",
      link: "",
      withImage: false,
      image: "",
      segment: "All", // Fixed: Changed from "all" to "All" to match segmentOptions
    },
    snackbar: {
      snackbar: false,
      timeout: 2000,
      text: null,
      color: "",
    },
    rules: {
      title: [
        (v) => !!v || "Title is required",
        (v) => (v && v.length >= 4) || "Title must be greater than 4",
      ],
      message: [(v) => !!v || "Message is required"],
      link: [
        (v) =>
          /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)|(^$)/.test(
            v
          ) || "Insert a valid url, like: https://maxprofitness.com/",
      ],
      segment: [(v) => !!v || "Segment is required"], // Added segment rule
    },
    segmentOptions: [
      // Added segment options
      { text: "All Users", value: "All" },
      { text: "Premium Users", value: "Premium Users" },
      { text: "Free Users", value: "Free Users" },
    ],
    dateFilterEnabled: false,
    dateFilterByRange: true,
    dateRange: [],
    beforeDate: null,
  }),
  computed: {
    lastLoginStartDateForPayload() {
      if (!this.dateFilterEnabled) return null;
      if (this.dateFilterByRange && Array.isArray(this.dateRange) && this.dateRange.length >= 1) {
        return this.dateRange[0] || null;
      }
      return null;
    },
    lastLoginEndDateForPayload() {
      if (!this.dateFilterEnabled) return null;
      if (this.dateFilterByRange && Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        return this.dateRange[1] || null;
      }
      if (!this.dateFilterByRange && this.beforeDate) {
        return this.beforeDate;
      }
      return null;
    },
    dateFilterIncomplete() {
      if (!this.dateFilterEnabled) return false;
      if (this.dateFilterByRange) {
        return !Array.isArray(this.dateRange) || this.dateRange.length < 2;
      }
      return !this.beforeDate;
    },
  },
  methods: {
    getNotificationImage(file) {
      const vm = this;
      if (file) {
        if (
          file.type === "image/jpeg" ||
          file.type === "image/jpg" ||
          file.type === "image/png"
        ) {
          const fr = new FileReader();
          fr.onload = (el) => {
            vm.notificationImageSelected.splice(0, 1, {
              img: el.target.result,
              filetype: file.type.split("/")[1],
            });
          };
          fr.readAsDataURL(file);
        } else {
          vm.messageSnack("Only .png and .jpeg files are accepted", "error");
        }
      }
    },
    async processNotification(send = false) {
      try {
        this.loading = true;

        let notificationData = {
          title: this.notification.title,
          message: this.notification.message,
          link: this.notification.link,
          image: this.notification.image,
          segment: this.notification.segment,
          dateFilterEnabled: this.dateFilterEnabled,
          dateFilterByRange: this.dateFilterByRange,
        };
        if (this.dateFilterEnabled) {
          notificationData.lastLoginStartDate = this.lastLoginStartDateForPayload;
          notificationData.lastLoginEndDate = this.lastLoginEndDateForPayload;
        }

        // Handle image upload only if it's a new image
        if (
          this.notification.withImage &&
          this.notificationImageSelected.length > 0
        ) {
          const imageData = this.notificationImageSelected[0];
          
          // Only upload if it's a data URL (new image uploaded by user)
          if (imageData.img.startsWith('data:')) {
            const imageLink = await this.$store.dispatch(
              "UploadImage",
              imageData.img
            );
            notificationData.image = imageLink;
          } else {
            // Keep the existing image URL (no upload needed)
            notificationData.image = imageData.img;
          }
        }
        
        if (send) {
          await this.$store.dispatch(
            "SendNotificationBySegment",
            notificationData
          );
        }

        if (this.editMode && this.editData) {
          // Only include fields that have changed
          const changedFields = {};
          Object.keys(notificationData).forEach(key => {
            // Special handling for image field
            if (key === 'image') {
              const currentImage = notificationData[key];
              const originalImage = this.editData[key];
              
              // Check if image has actually changed
              if (currentImage !== originalImage) {
                changedFields[key] = currentImage;
              }
            } else {
              // For other fields, simple comparison
              if (notificationData[key] !== this.editData[key]) {
                changedFields[key] = notificationData[key];
              }
            }
          });
          
          // Only update if there are changes
          if (Object.keys(changedFields).length > 0) {
            console.log({
              id: this.editData.id,
              ...changedFields
            });
            
            await this.$store.dispatch("UpdateNotification", {
              id: this.editData.id,
              ...changedFields
            });
            this.messageSnack("Notification updated successfully!", "success");
            this.$emit("notificationUpdated", this.editData.id);
          } else {
            this.messageSnack("No changes detected", "info");
          }
        } else {
          // Create new notification
          await this.$store.dispatch("CreateNotification", notificationData);
          this.messageSnack("Notification created successfully!", "success");
          this.$emit("notificationCreated", `segment_${Date.now()}`);
        }
        
        this.loading = false;
        this.resetForm();
        this.closeDialog();
      } catch (error) {
        this.loading = false;
        this.messageSnack(`Error ${this.editMode ? 'updating' : 'creating'} notification`, "error");
      }
    },
    closeDialog() {
      let vm = this;
      vm.resetForm();
      vm.dialog.show = false;
      vm.$emit("dialogClosed");
    },
    resetForm() {
      let vm = this;
      if (vm.editMode && vm.editData) {
        // Populate form with existing data for editing
        vm.notification = {
          title: vm.editData.title || "",
          message: vm.editData.message || "",
          link: vm.editData.link || "",
          withImage: !!vm.editData.image,
          image: vm.editData.image || "",
          segment: vm.editData.segment || "All",
        };
        // If there's an existing image, show it
        if (vm.editData.image) {
          vm.notificationImageSelected = [{
            img: vm.editData.image,
            filetype: "existing"
          }];
        } else {
          vm.notificationImageSelected = [];
        }
      } else {
        // Reset form for creating new notification
        vm.notification = {
          title: "",
          message: "",
          link: "",
          withImage: false,
          image: "",
          segment: "All",
        };
        vm.notificationImageSelected = [];
      }
      vm.dateFilterEnabled = false;
      vm.dateFilterByRange = true;
      vm.dateRange = [];
      vm.beforeDate = null;
      if (vm.initialDateFilter && vm.initialDateFilter.enabled) {
        vm.dateFilterEnabled = true;
        vm.dateFilterByRange = !!(
          vm.initialDateFilter.startDate &&
          vm.initialDateFilter.endDate &&
          vm.initialDateFilter.byRange
        );
        if (vm.dateFilterByRange && vm.initialDateFilter.startDate && vm.initialDateFilter.endDate) {
          vm.dateRange = [vm.initialDateFilter.startDate, vm.initialDateFilter.endDate];
          vm.beforeDate = null;
        } else if (vm.initialDateFilter.endDate) {
          vm.beforeDate = vm.initialDateFilter.endDate;
          vm.dateRange = [];
        }
      }
      if (vm.$refs.form) {
        vm.$refs.form.resetValidation();
      }
    },
    messageSnack(text, type) {
      let vm = this;
      vm.snackbar.text = text;
      vm.snackbar.color = type === "error" ? "red darken-2" : "success";
      vm.snackbar.snackbar = true;
    },
  },
  watch: {
    "dialog.show"(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
    editData: {
      handler(newVal) {
        if (newVal && this.editMode) {
          this.resetForm();
        }
      },
      immediate: true
    }
  },
};
</script> 